import React, { useCallback, useEffect, useState } from "react";
import { ArtifactProto } from "build-ts/chat_session";
import { DataFrameAnalysisProto } from "build-ts/dataframe";
import { ArtifactOpenOpts } from "modules/interfaces";
import DataFrameAnalysisComponent from "../components/DataFrameAnalysis";
import { TAB_ID, TabId, isValidTabId } from "../utils/dataFrameCodeUtils";
import { downloadReportFile } from "services/DataService";
import { ReportDownloadFormat } from "services/serviceUtils";

interface DataFrameAnalysisContainerProps {
  data: DataFrameAnalysisProto;
  onClose?: () => void;
  /** No-op in the panel context — collapse is handled by the artifact strip. */
  collapseSignal?: number;
  /** Artifact registry lookup — forwarded to InputsTab for type-based interaction decisions. */
  lookupFn?: (name: string) => ArtifactProto | undefined;
  /** Populate a parent artifact in-place so the Inputs tab can render its data inline. */
  onPopulateInput?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => Promise<void> | void;
  initialActiveTab?: string;
  initialResultPillName?: string;
  /** Persisted selected input name (parent_header.name) — restored when the panel reopens. */
  initialInputPillName?: string;
  /** Whether the artifact panel is currently expanded to full width */
  isExpanded?: boolean;
  /** Called when the expand/collapse toggle button is clicked */
  onExpandToggle?: () => void;
  /** Opens an artifact in the panel, optionally navigating to a specific tab/sub-resource. */
  onOpenArtifact?: (artifact: ArtifactProto, opts?: ArtifactOpenOpts) => void;
  // Called whenever the user changes the active tab, result pill, or input selection so
  // the parent can persist the view state and restore it when the user returns.
  onArtifactTabViewChange?: (view: {
    activeTab: string;
    selectedResultPillName: string;
    selectedInputPillName?: string;
  }) => void;
  /** Chat session ID — passed through to DocxTab for report download. */
  chatId?: string;
  /** When true, suppress the ArtifactPanelHeader — used when embedded inside another card. */
  hideHeader?: boolean;
}

/**
 * Container for DataFrameAnalysis artifacts.
 * Owns tab selection state and syncs it from props (initialActiveTab /
 * initialSelectedItemIndex) so the parent can persist and restore view state.
 */
const DataFrameAnalysisContainer: React.FC<DataFrameAnalysisContainerProps> = ({
  data,
  lookupFn,
  onPopulateInput,
  initialActiveTab,
  initialResultPillName,
  initialInputPillName,
  isExpanded,
  onExpandToggle,
  onArtifactTabViewChange,
  onOpenArtifact,
  chatId,
  hideHeader,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>(isValidTabId(initialActiveTab) ? initialActiveTab : TAB_ID.results);
  const [selectedResultPillName, setSelectedResultPillName] = useState(initialResultPillName ?? "");
  const [selectedInputName, setSelectedInputName] = useState(initialInputPillName ?? "");

  useEffect(() => {
    if (isValidTabId(initialActiveTab)) {
      setActiveTab(initialActiveTab);
    }
  }, [initialActiveTab]);

  useEffect(() => {
    if (initialResultPillName !== undefined) {
      setSelectedResultPillName(initialResultPillName);
    }
  }, [initialResultPillName]);

  useEffect(() => {
    if (initialInputPillName !== undefined) {
      setSelectedInputName(initialInputPillName);
    }
  }, [initialInputPillName]);

  const handleTabChange = (tab: TabId) => {
    setActiveTab(tab);
    onArtifactTabViewChange?.({ activeTab: tab, selectedResultPillName, selectedInputPillName: selectedInputName });
  };

  const handleResultPillChange = (name: string) => {
    setSelectedResultPillName(name);
    onArtifactTabViewChange?.({ activeTab, selectedResultPillName: name, selectedInputPillName: selectedInputName });
  };

  const handleInputPillChange = (name: string) => {
    setSelectedInputName(name);
    onArtifactTabViewChange?.({ activeTab, selectedResultPillName, selectedInputPillName: name });
  };

  const handleDownloadReport = useCallback(
    async (format: ReportDownloadFormat, reportName?: string): Promise<void> => {
      if (chatId && data.header?.id) {
        await downloadReportFile(chatId, data.header.id, format, data.header.name, reportName);
      }
    },
    [chatId, data.header?.id, data.header?.name, data.reports]
  );

  return (
    <DataFrameAnalysisComponent
      data={data}
      activeTab={activeTab}
      selectedResultPillName={selectedResultPillName}
      selectedInputName={selectedInputName}
      onTabChange={handleTabChange}
      onResultPillChange={handleResultPillChange}
      onInputPillChange={handleInputPillChange}
      lookupFn={lookupFn}
      onPopulateInput={onPopulateInput}
      onOpenArtifact={onOpenArtifact}
      isExpanded={isExpanded}
      onExpandToggle={onExpandToggle}
      onDownloadReport={handleDownloadReport}
      hideHeader={hideHeader}
    />
  );
};

export default DataFrameAnalysisContainer;
